import React, { useState } from "react";
import useGameBase from '../../hooks/useGameBase';

function MathsQuizGame({ gameState, gamePlayers, user, disabled, onGameAction, onPlayAgain, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const { question, phase, currentQuestion, finished, scores, answers, multipleGuesses, correctAnswer, roundScores, answerTimes, choices, answerMode } = gameState;

  // Helper: get only answers for this question (if answers is an object of arrays, filter by currentQuestion)
  // If answers is { [playerId]: [ans1, ans2, ...] }, show only the last answer for this question
  const getCurrentAnswer = (playerId) => {
    const ans = answers && answers[playerId];
    if (multipleGuesses && Array.isArray(ans)) {
      // If server resets answers per question, this is just the current question's guesses
      // If not, show only guesses since last reveal (last N guesses)
      return ans.length > 0 ? ans : [];
    } else if (typeof ans === 'string') {
      return ans;
    }
    return null;
  };
  const totalQuestions = gameState.numQuestions || 10;
  const [input, setInput] = useState("");
  const [mcSubmitted, setMcSubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(gameState.timeLimit || 30);
  const timeLimit = gameState.timeLimit || 30;

  // For multiple guesses, track if user has already got it correct
  const userAnswers = answers && answers[user.socketId];
  const userCorrect = multipleGuesses && Array.isArray(userAnswers) && userAnswers.some(a => a.trim() === correctAnswer);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || (submitted && !multipleGuesses) || phase !== "question" || finished) return;
    if (multipleGuesses && userCorrect) return; // Already correct, don't allow more
    submitAction(input.trim()).catch(()=>{});
    setSubmitted(true);
    setInput("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleMCClick = (choice) => {
    if (disabled || mcSubmitted || (multipleGuesses && userCorrect) || phase !== "question" || finished) return;
    submitAction(choice).catch(()=>{});
    setMcSubmitted(true);
    setSubmitted(true);
  };

  React.useEffect(() => {
    // Reset input and submission state on new question
    setInput("");
    setSubmitted(false);
    setMcSubmitted(false);
  }, [currentQuestion]);

  React.useEffect(() => {
    setTimeLeft(timeLimit);
    if (phase === "question" && !finished) {
      let start = Date.now();
      let raf;
      const tick = () => {
        const elapsed = (Date.now() - start) / 1000;
        const pct = Math.min(elapsed / timeLimit, 1);
        setTimeLeft(Math.max(0, Math.ceil(timeLimit - elapsed)));
        if (pct < 1 && phase === "question" && !finished) {
          raf = requestAnimationFrame(tick);
        }
      };
      raf = requestAnimationFrame(tick);
      return () => raf && cancelAnimationFrame(raf);
    }
  }, [currentQuestion, phase, finished, timeLimit]);

  // Ref for answer input
  const inputRef = React.useRef(null);

  // Auto-focus input when question phase is active and not finished
  React.useEffect(() => {
    if (
      phase === "question" &&
      !finished &&
      inputRef.current
    ) {
      inputRef.current.focus();
    }
  }, [phase, finished, currentQuestion]);

  return (
    <div className="w-full max-w-md mx-auto mt-6 p-4 bg-white dark:bg-gray-900 rounded shadow text-gray-900 dark:text-gray-100">
      {phase === "finished" || finished ? (
        <div className="text-lg font-bold mb-2">Quiz Finished!</div>
      ) : (
        <div className="mb-2 text-gray-700 dark:text-gray-200">Question {currentQuestion + 1} of {totalQuestions}</div>
      )}
      {multipleGuesses && phase === "question" && !finished && <div className="mb-2 text-xs text-blue-700 dark:text-blue-300">Keep guessing until you get it right or time runs out!</div>}
      {phase === "question" && !finished && (
        <>
          <div className="mb-4 text-lg font-semibold">{question || <span className="italic text-gray-400 dark:text-gray-500">Waiting for question...</span>}</div>
          {/* Multiple Choice UI */}
          {answerMode === "Multiple Choice" && Array.isArray(choices) && choices.length > 0 ? (
            <div className="flex flex-col gap-2 mb-4">
              {choices.map((choice, idx) => (
                <button
                  key={idx}
                  className={`w-full px-4 py-2 rounded border font-semibold text-lg ${submitted ? (choice === correctAnswer ? "bg-green-600 text-white" : "bg-red-200 text-gray-700") : "bg-blue-100 hover:bg-blue-200 text-blue-900"}`}
                  disabled={disabled || submitted || mcSubmitted || (multipleGuesses && userCorrect)}
                  onClick={() => handleMCClick(choice)}
                >
                  {choice}
                </button>
              ))}
            </div>
          ) : (
            !(submitted && !multipleGuesses) && (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  ref={inputRef}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="border rounded px-3 py-2 flex-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  disabled={disabled || (multipleGuesses && userCorrect)}
                  autoFocus
                  autoComplete="off"
                  placeholder="Your answer"
                />
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded"
                  disabled={disabled || (multipleGuesses && userCorrect)}
                >
                  {multipleGuesses ? (userCorrect ? "Correct!" : "Guess") : "Submit"}
                </button>
              </form>
            )
          )}
          {multipleGuesses && Array.isArray(userAnswers) && userAnswers.length > 0 && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">Your guesses: {userAnswers.join(", ")}</div>
          )}
          {!multipleGuesses && submitted && <div className="mt-2 text-blue-600 dark:text-blue-300">Answer submitted! Waiting for others...</div>}
          {multipleGuesses && userCorrect && <div className="mt-2 text-green-600 dark:text-green-400 font-semibold">You got it right!</div>}
          {/* Progress bar for time limit */}
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded mt-4 overflow-hidden">
            <div
              className="h-full bg-yellow-400 transition-none"
              style={{ width: `${(1 - timeLeft / timeLimit) * 100}%`, transition: 'width 0.1s linear' }}
            ></div>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 text-right">{timeLeft}s left</div>
        </>
      )}
      {phase === "reveal" && !finished && (
        <div className="mt-4">
          <div className="text-lg font-bold mb-2">The correct answer was: <span className="text-green-700 dark:text-green-400">{correctAnswer}</span></div>
          <div className="mb-2 font-semibold">Round Results:</div>
          <ul className="mb-2">
            {gamePlayers
              .map(p => {
                let gotCorrect = false;
                const ans = getCurrentAnswer(p.socketId);
                if (multipleGuesses && Array.isArray(ans)) {
                  gotCorrect = ans.some(a => a.trim() === correctAnswer);
                } else if (typeof ans === 'string') {
                  gotCorrect = ans.trim() === correctAnswer;
                }
                // Get answer time in ms for this player/round
                let ms = answerTimes && answerTimes[currentQuestion] && answerTimes[currentQuestion][p.socketId];
                let timeStr = ms != null ? `${(ms/1000).toFixed(2)}s` : null;
                return {
                  socketId: p.socketId,
                  username: p.username,
                  gotCorrect,
                  answer: ans,
                  roundScore: roundScores ? roundScores[p.socketId] || 0 : 0,
                  answerTime: timeStr,
                };
              })
              .sort((a, b) => b.roundScore - a.roundScore)
              .map((player, i) => (
                <li key={player.socketId} className="mb-1">
                  <span className="font-semibold">{user.socketId === player.socketId || (user.userId && user.userId === player.userId) ? "You" : player.username}</span>
                  {": "}
                  <span className={player.gotCorrect ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}>
                    {player.gotCorrect ? "Correct" : "Wrong"}
                  </span>
                  {multipleGuesses && Array.isArray(player.answer) ? (
                    <span className="ml-2 text-xs text-gray-600 dark:text-gray-300">[{player.answer.join(", ")}]</span>
                  ) : player.answer ? (
                    <span className="ml-2 text-xs text-gray-600 dark:text-gray-300">[{player.answer}]</span>
                  ) : null}
                  {player.answerTime && (
                    <span className="ml-2 text-xs text-blue-700 dark:text-blue-300">({player.answerTime})</span>
                  )}
                  <span className="ml-2">Score: {player.roundScore}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
      {phase === "finished" || finished ? (
        <div className="mt-4">
          <div className="mb-2">Final Scores:</div>
          <ul className="mb-2">
            {gamePlayers
              .map(p => ({
                socketId: p.socketId,
                username: p.username,
                score: scores[p.socketId] || 0
              }))
              .sort((a, b) => b.score - a.score)
              .map((player, i) => (
                <li key={player.socketId} className="mb-1">
                  <span className="font-semibold">{user.socketId === player.socketId || (user.userId && user.userId === player.userId) ? "You" : player.username}</span>: {player.score}
                </li>
              ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

MathsQuizGame.Guide = function MathsQuizGameGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Maths Quiz</h3>
      <ul className="list-disc ml-6">
        <li>Answer the math question as quickly as possible.</li>
        <li>Points are awarded for correct answers and speed.</li>
        <li>Highest score at the end wins.</li>
      </ul>
    </div>
  );
};

export default MathsQuizGame;
